/**
 * Notification Store (Zustand)
 *
 * Global state management for notifications:
 * - Real-time notifications via Socket.IO
 * - Notification history
 * - Unread count tracking
 * - Mark as read/unread
 *
 * Integrates with Socket.IO for real-time updates.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Notification interface
 */
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  metadata?: Record<string, any>;
  /**
   * Origin of the notification, used by the bell to merge this store's
   * socket-pushed items with the server's persisted list without double
   * counting. Absent (undefined) is treated as `"local"`.
   */
  source?: 'local' | 'server';
}

export type NotificationType = 'info' | 'success' | 'warning' | 'error' | 'billing' | 'resource' | 'system';

/**
 * Notification Store State
 */
interface NotificationState {
  // State
  notifications: Notification[];
  unreadCount: number;

  // Actions
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: string) => void;
  markAsUnread: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  clearAll: () => void;
  updateUnreadCount: () => void;
}

/**
 * Maximum notifications to keep in store
 */
const MAX_NOTIFICATIONS = 100;

/**
 * Notification Store
 */
export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      // Initial state
      notifications: [],
      unreadCount: 0,

      /**
       * Add new notification
       */
      addNotification: (notification: Notification) => {
        set((state) => {
          // Check if notification already exists
          const exists = state.notifications.some((n) => n.id === notification.id);
          if (exists) {
            return state;
          }

          // Add to beginning of array
          const notifications = [notification, ...state.notifications];

          // Keep only latest MAX_NOTIFICATIONS
          const trimmed = notifications.slice(0, MAX_NOTIFICATIONS);

          // Update unread count
          const unreadCount = trimmed.filter((n) => !n.read).length;

          return {
            notifications: trimmed,
            unreadCount,
          };
        });
      },

      /**
       * Mark notification as read
       */
      markAsRead: (notificationId: string) => {
        set((state) => {
          const notifications = state.notifications.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n
          );

          const unreadCount = notifications.filter((n) => !n.read).length;

          return {
            notifications,
            unreadCount,
          };
        });
      },

      /**
       * Mark notification as unread
       */
      markAsUnread: (notificationId: string) => {
        set((state) => {
          const notifications = state.notifications.map((n) =>
            n.id === notificationId ? { ...n, read: false } : n
          );

          const unreadCount = notifications.filter((n) => !n.read).length;

          return {
            notifications,
            unreadCount,
          };
        });
      },

      /**
       * Mark all notifications as read
       */
      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      /**
       * Remove notification
       */
      removeNotification: (notificationId: string) => {
        set((state) => {
          const notifications = state.notifications.filter((n) => n.id !== notificationId);
          const unreadCount = notifications.filter((n) => !n.read).length;

          return {
            notifications,
            unreadCount,
          };
        });
      },

      /**
       * Clear all notifications
       */
      clearAll: () => {
        set({
          notifications: [],
          unreadCount: 0,
        });
      },

      /**
       * Update unread count
       */
      updateUnreadCount: () => {
        const { notifications } = get();
        const unreadCount = notifications.filter((n) => !n.read).length;
        set({ unreadCount });
      },
    }),
    {
      name: 'wicloud_notifications',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
      }),
    }
  )
);

/**
 * Hook to easily access notification actions
 */
export const useNotifications = () => {
  const notifications = useNotificationStore((state) => state.notifications);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const addNotification = useNotificationStore((state) => state.addNotification);
  const markAsRead = useNotificationStore((state) => state.markAsRead);
  const markAsUnread = useNotificationStore((state) => state.markAsUnread);
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead);
  const removeNotification = useNotificationStore((state) => state.removeNotification);
  const clearAll = useNotificationStore((state) => state.clearAll);

  return {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    removeNotification,
    clearAll,
  };
};
